// VRG-712 — the arithmetic behind Visitor history's columns and the journey drawer, kept pure
// (no React, no MUI, no path aliases) so `test/website-visitor-journey.guard.spec.ts` can load
// it type-stripped with node's own test runner.
//
// UNKNOWN STAYS UNKNOWN. A page PostHog never saw the visitor leave has `seconds: null`
// (website-time-on-page): it is drawn as "unknown", never as 0, and a total that skips such a
// page is a floor and says so (`unknownPages`). Same rule as VRG-689's `sessionTime`.

/** The fields of a WebsiteSession this model reads (structural, so a fixture can be small). */
export interface JourneySessionInput {
  sessionId: string
  startedAt: string
  lastSeenAt: string
  entryPath: string | null
  currentPath: string | null
  referrer: string | null
  utmSource: string | null
  pageviews: number
  pages: ReadonlyArray<{ path: string | null; at: string; seconds: number | null }> | null
}

export interface JourneyStep {
  path: string | null
  at: string
  /** Seconds on the page; null = unknown. */
  seconds: number | null
  /** 0..1 of the session's longest timed page, for the time bar. 0 when unknown. */
  share: number
  /** The session's first page (where they landed). */
  isEntry: boolean
  /** The session's last page (where they left, or are now). */
  isExit: boolean
  /** The last page of a session active in the last LIVE_WINDOW_MINUTES: they are on it now. */
  onItNow: boolean
}

export interface JourneySummary {
  steps: JourneyStep[]
  /** Sum of the timed pages; null when none is timed. */
  knownSeconds: number | null
  /** Pages with no time (the total is a floor when > 0). */
  unknownPages: number
  entryPath: string | null
  exitPath: string | null
  /** PostHog could not read this session's pages: no steps to draw, and say so. */
  pagesUnavailable: boolean
}

/** Same window as the API's Live now (LIVE_WINDOW_MINUTES). */
export const LIVE_WINDOW_MINUTES = 5

/**
 * Where a session came from, as Visitor history labels it: the utm_source when the landing URL
 * had one, else the referring domain, else "Direct / none".
 */
export const sessionSourceLabel = (s: Pick<JourneySessionInput, 'referrer' | 'utmSource'>): string =>
  s.utmSource ? `utm: ${s.utmSource}` : s.referrer ? s.referrer : 'Direct / none'

export const summariseSession = (s: JourneySessionInput, now: Date = new Date()): JourneySummary => {
  const pages = s.pages ?? []
  const live = now.getTime() - Date.parse(s.lastSeenAt) < LIVE_WINDOW_MINUTES * 60_000
  const timed = pages.filter((p) => p.seconds !== null).map((p) => p.seconds as number)
  const max = timed.length ? Math.max(...timed) : 0
  const steps = pages.map((p, i): JourneyStep => {
    const isExit = i === pages.length - 1
    return {
      path: p.path,
      at: p.at,
      seconds: p.seconds,
      share: p.seconds !== null && max > 0 ? p.seconds / max : 0,
      isEntry: i === 0,
      isExit,
      onItNow: isExit && live && p.seconds === null,
    }
  })
  return {
    steps,
    knownSeconds: timed.length ? timed.reduce((a, b) => a + b, 0) : null,
    // A page they are on right now is not "unknown": it is still running.
    unknownPages: steps.filter((p) => p.seconds === null && !p.onItNow).length,
    entryPath: pages[0]?.path ?? s.entryPath,
    exitPath: pages.length ? pages[pages.length - 1].path : s.currentPath,
    pagesUnavailable: s.pages === null,
  }
}

/** Across a visitor's sessions: the timed seconds and the untimed pages, summed. */
export const journeyTotals = (
  sessions: ReadonlyArray<JourneySessionInput>,
  now: Date = new Date(),
): { knownSeconds: number | null; unknownPages: number; pages: number } => {
  let known: number | null = null
  let unknown = 0
  let pages = 0
  for (const s of sessions) {
    const sum = summariseSession(s, now)
    if (sum.knownSeconds !== null) known = (known ?? 0) + sum.knownSeconds
    unknown += sum.unknownPages
    pages += sum.pagesUnavailable ? s.pageviews : sum.steps.length
  }
  return { knownSeconds: known, unknownPages: unknown, pages }
}

/**
 * The Table's page model → the API's `limit`/`offset`. The shared footer also offers "All"
 * (-1); the API serves at most 100 a page, so "All" asks for 100 and the footer is put back to
 * 100 by the caller (never a request the server would silently clamp).
 */
export const VISITOR_PAGE_SIZES = [25, 50, 100] as const
export const VISITOR_PAGE_MAX = 100

export const toVisitorPage = (model: { page: number; pageSize: number }): { page: number; pageSize: number; limit: number; offset: number } => {
  const pageSize = model.pageSize === -1 || model.pageSize > VISITOR_PAGE_MAX ? VISITOR_PAGE_MAX : Math.max(1, model.pageSize)
  const page = Math.max(0, model.page)
  return { page, pageSize, limit: pageSize, offset: page * pageSize }
}
